<template>
  <div>
    <b-card no-body>
      <b-card-body>
        <validation-observer ref="refFormObserver">
          <div class="d-flex justify-content-between align-items-center mb-1">
            <h5 class="mb-0">{{ $t("dmp.bellOptions.title") }}</h5>
            <div class="d-flex gap-1">
              <b-button
                v-if="!isTemplateMode"
                variant="primary"
                size="sm"
                @click="saveBellOptions"
                :disabled="isSaving"
              >
                <b-spinner v-if="isSaving" small class="mr-25" />
                <feather-icon v-else icon="SaveIcon" size="14" class="mr-25" />
                {{ isSaving ? $t("actions.saving") : $t("actions.save") }}
              </b-button>
            </div>
          </div>

          <!-- Loading spinner -->
          <div v-if="isLoading" class="d-flex justify-content-center p-3">
            <b-spinner variant="primary" :label="$t('dmp.bellOptions.loading')" />
            <span class="ml-2">{{ $t("dmp.bellOptions.loading") }}...</span>
          </div>

          <b-form v-else @submit.prevent="saveBellOptions">
            <b-row>
              <!-- Options Section -->
              <b-col cols="12">
                <b-card class="mb-2 border">
                  <h6 class="mb-3 font-weight-bold">
                    {{ $t("dmp.bellOptions.optionsSection") }}
                  </h6>

                  <b-row>
                    <!-- Fire Bell Action -->
                    <b-col cols="12" md="6" lg="4" class="mb-2">
                      <validation-provider
                        #default="validationContext"
                        :name="$t('dmp.bellOptions.fireBellAction')"
                        rules="required"
                      >
                        <b-form-group
                          :label="$t('dmp.bellOptions.fireBellAction')"
                          label-for="fire-bell-action"
                        >
                          <b-form-select
                            id="fire-bell-action"
                            v-model="bellOptions.fireBellAction"
                            :options="bellActionOptions"
                            :state="getValidationState(validationContext)"
                          />
                          <b-form-invalid-feedback>
                            {{ validationContext.errors[0] }}
                          </b-form-invalid-feedback>
                        </b-form-group>
                      </validation-provider>
                    </b-col>

                    <!-- Burglary Bell Action -->
                    <b-col cols="12" md="6" lg="4" class="mb-2">
                      <validation-provider
                        #default="validationContext"
                        :name="$t('dmp.bellOptions.burglaryBellAction')"
                        rules="required"
                      >
                        <b-form-group
                          :label="$t('dmp.bellOptions.burglaryBellAction')"
                          label-for="burglary-bell-action"
                        >
                          <b-form-select
                            id="burglary-bell-action"
                            v-model="bellOptions.burglaryBellAction"
                            :options="bellActionOptions"
                            :state="getValidationState(validationContext)"
                          />
                          <b-form-invalid-feedback>
                            {{ validationContext.errors[0] }}
                          </b-form-invalid-feedback>
                        </b-form-group>
                      </validation-provider>
                    </b-col>

                    <!-- Supervisory Bell Action -->
                    <b-col cols="12" md="6" lg="4" class="mb-2">
                      <validation-provider
                        #default="validationContext"
                        :name="$t('dmp.bellOptions.supervisoryBellAction')"
                        rules="required"
                      >
                        <b-form-group
                          :label="$t('dmp.bellOptions.supervisoryBellAction')"
                          label-for="supervisory-bell-action"
                        >
                          <b-form-select
                            id="supervisory-bell-action"
                            v-model="bellOptions.supervisoryBellAction"
                            :options="bellActionOptions"
                            :state="getValidationState(validationContext)"
                          />
                          <b-form-invalid-feedback>
                            {{ validationContext.errors[0] }}
                          </b-form-invalid-feedback>
                        </b-form-group>
                      </validation-provider>
                    </b-col>

                    <!-- Panic Bell Action -->
                    <b-col cols="12" md="6" lg="4" class="mb-2">
                      <validation-provider
                        #default="validationContext"
                        :name="$t('dmp.bellOptions.panicBellAction')"
                        rules="required"
                      >
                        <b-form-group
                          :label="$t('dmp.bellOptions.panicBellAction')"
                          label-for="panic-bell-action"
                        >
                          <b-form-select
                            id="panic-bell-action"
                            v-model="bellOptions.panicBellAction"
                            :options="bellActionOptions"
                            :state="getValidationState(validationContext)"
                          />
                          <b-form-invalid-feedback>
                            {{ validationContext.errors[0] }}
                          </b-form-invalid-feedback>
                        </b-form-group>
                      </validation-provider>
                    </b-col>

                    <!-- Emergency Bell Action -->
                    <b-col cols="12" md="6" lg="4" class="mb-2">
                      <validation-provider
                        #default="validationContext"
                        :name="$t('dmp.bellOptions.emergencyBellAction')"
                        rules="required"
                      >
                        <b-form-group
                          :label="$t('dmp.bellOptions.emergencyBellAction')"
                          label-for="emergency-bell-action"
                        >
                          <b-form-select
                            id="emergency-bell-action"
                            v-model="bellOptions.emergencyBellAction"
                            :options="bellActionOptions"
                            :state="getValidationState(validationContext)"
                          />
                          <b-form-invalid-feedback>
                            {{ validationContext.errors[0] }}
                          </b-form-invalid-feedback>
                        </b-form-group>
                      </validation-provider>
                    </b-col>

                    <!-- Aux 1 Bell Action -->
                    <b-col cols="12" md="6" lg="4" class="mb-2">
                      <validation-provider
                        #default="validationContext"
                        :name="$t('dmp.bellOptions.aux1BellAction')"
                        rules="required"
                      >
                        <b-form-group
                          :label="$t('dmp.bellOptions.aux1BellAction')"
                          label-for="aux1-bell-action"
                        >
                          <b-form-select
                            id="aux1-bell-action"
                            v-model="bellOptions.aux1BellAction"
                            :options="bellActionOptions"
                            :state="getValidationState(validationContext)"
                          />
                          <b-form-invalid-feedback>
                            {{ validationContext.errors[0] }}
                          </b-form-invalid-feedback>
                        </b-form-group>
                      </validation-provider>
                    </b-col>

                    <!-- Aux 2 Bell Action -->
                    <b-col cols="12" md="6" lg="4" class="mb-2">
                      <validation-provider
                        #default="validationContext"
                        :name="$t('dmp.bellOptions.aux2BellAction')"
                        rules="required"
                      >
                        <b-form-group
                          :label="$t('dmp.bellOptions.aux2BellAction')"
                          label-for="aux2-bell-action"
                        >
                          <b-form-select
                            id="aux2-bell-action"
                            v-model="bellOptions.aux2BellAction"
                            :options="bellActionOptions"
                            :state="getValidationState(validationContext)"
                          />
                          <b-form-invalid-feedback>
                            {{ validationContext.errors[0] }}
                          </b-form-invalid-feedback>
                        </b-form-group>
                      </validation-provider>
                    </b-col>

                    <!-- carbon Monoxide Action -->
                    <b-col cols="12" md="6" lg="4" class="mb-2">
                      <validation-provider
                        #default="validationContext"
                        :name="$t('dmp.bellOptions.carbonMonoxideAction')"
                        rules="required"
                      >
                        <b-form-group
                          :label="$t('dmp.bellOptions.carbonMonoxideAction')"
                          label-for="aux2-bell-action"
                        >
                          <b-form-select
                            id="aux2-bell-action"
                            v-model="bellOptions.carbonMonoxideAction"
                            :options="bellActionOptions"
                            :state="getValidationState(validationContext)"
                          />
                          <b-form-invalid-feedback>
                            {{ validationContext.errors[0] }}
                          </b-form-invalid-feedback>
                        </b-form-group>
                      </validation-provider>
                    </b-col>
                  </b-row>
                </b-card>
              </b-col>

              <!-- Miscellaneous Options Section -->
              <b-col cols="12">
                <b-card class="mb-2 border">
                  <h6 class="mb-3 font-weight-bold">
                    {{ $t("dmp.bellOptions.miscellaneousOptions") }}
                  </h6>

                  <b-row>
                    <!-- Bell Cutoff Time -->
                    <b-col cols="12" md="4" class="mb-2">
                      <validation-provider
                        #default="validationContext"
                        :name="$t('dmp.bellOptions.bellCutoffTime')"
                        rules="required|integer|min_value:1|max_value:99"
                      >
                        <b-form-group
                          :label="$t('dmp.bellOptions.bellCutoffTime')"
                          label-for="bell-cutoff-time"
                        >
                          <b-form-input
                            id="bell-cutoff-time"
                            v-model="bellOptions.cutOffTime"
                            :state="getValidationState(validationContext)"
                            maxlength="2"
                            @blur="formatCutoffTime"
                          />
                          <b-form-text class="text-muted">
                            <small>{{ $t("dmp.bellOptions.bellCutoffTimeHint") }}</small>
                          </b-form-text>
                          <b-form-invalid-feedback>
                            {{ validationContext.errors[0] }}
                          </b-form-invalid-feedback>
                        </b-form-group>
                      </validation-provider>
                    </b-col>

                    <!-- Automatic Bell Test -->
                    <b-col cols="12" md="4" class="mb-2">
                      <b-form-group
                        :label="$t('dmp.bellOptions.automaticBellTest')"
                        label-for="automatic-bell-test"
                      >
                        <b-form-checkbox
                          id="automatic-bell-test"
                          v-model="automaticBellTestEnabled"
                          switch
                          class="mt-1"
                        >
                          {{
                            automaticBellTestEnabled
                              ? $t("dmp.common.yes")
                              : $t("dmp.common.no")
                          }}
                        </b-form-checkbox>
                      </b-form-group>
                    </b-col>

                    <!-- Bell Output -->
                    <b-col cols="12" md="4" class="mb-2">
                      <validation-provider
                        #default="validationContext"
                        :name="$t('dmp.bellOptions.bellOutput')"
                        rules="required|integer|min_value:0|max_value:999"
                      >
                        <b-form-group
                          :label="$t('dmp.bellOptions.bellOutput')"
                          label-for="bell-output"
                        >
                          <b-form-input
                            id="bell-output"
                            v-model="bellOptions.bellOutput"
                            :state="getValidationState(validationContext)"
                            maxlength="3"
                            @blur="formatBellOutput"
                          />
                          <b-form-text class="text-muted">
                            <small>{{ $t("dmp.bellOptions.bellOutputHint") }}</small>
                          </b-form-text>
                          <b-form-invalid-feedback>
                            {{ validationContext.errors[0] }}
                          </b-form-invalid-feedback>
                        </b-form-group>
                      </validation-provider>
                    </b-col>
                  </b-row>
                </b-card>
              </b-col>
            </b-row>
          </b-form>
        </validation-observer>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount, inject, getCurrentInstance, watch } from "@vue/composition-api";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import { useToast } from "vue-toastification/composition";
import formValidation from "@core/comp-functions/forms/form-validation";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import store from "@/store";
import { useTemplateMode } from "./useTemplateMode";
import { formatToTwoDigits, formatToThreeDigits } from "./usePanelConfig";

export default {
  name: "DmpPanelsConfigBellOptions",
  components: {
    ValidationObserver,
    ValidationProvider,
    ToastificationContent,
  },
  props: {
    uuid: {
      type: String,
      required: true,
    },
    bellOptionsData: {
      type: Object,
      default: () => ({}),
    },
    panelId: {
      type: [Number, String],
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    source: {
      type: String,
      default: "default",
    },
  },
  setup(props, { emit }) {
    const { t } = useI18nUtils();
    const toast = useToast();
    const vm = getCurrentInstance();
    const proxy = vm.root.proxy;
    const { isTemplateMode, emitTemplateUpdate } = useTemplateMode(props, emit);

    // Form validation
    const { refFormObserver, getValidationState } = formValidation();

    // State
    const isSaving = ref(false);
    const isLoading = ref(false);
    const automaticBellTestEnabled = ref(false);

    // Bell options data
    const bellOptions = ref({
      cutOffTime: "15",
      automaticBellTest: "N",
      fireBellAction: "NONE",
      burglaryBellAction: "NONE",
      supervisoryBellAction: "NONE",
      panicBellAction: "NONE",
      emergencyBellAction: "NONE",
      aux1BellAction: "NONE",
      aux2BellAction: "NONE",
      carbonMonoxideAction: "NONE",
      bellOutput: "000",
    });

    // GET format (from API): N, S, P, T, 4
    // POST/Display format: NONE, STEADY, PULSE, TEMPORAL_3, TEMPORAL_4
    const bellActionFromApi = (value) => {
      const map = {
        N: "NONE",
        S: "STEADY",
        P: "PULSE",
        T: "TEMPORAL_3",
        4: "TEMPORAL_4",
      };
      return map[value] || "NONE";
    };

    // Bell action options
    const bellActionOptions = computed(() => [
      { value: "NONE", text: "NONE" },
      { value: "PULSE", text: "PULSE" },
      { value: "STEADY", text: "STEADY" },
      { value: "TEMPORAL_3", text: "TEMPORAL 3" },
      { value: "TEMPORAL_4", text: "TEMPORAL 4" },
    ]);

    // Load bell options data
    const loadBellOptions = () => {
      if (props.bellOptionsData && Object.keys(props.bellOptionsData).length > 0) {
        bellOptions.value = {
          cutOffTime: props.bellOptionsData.cutOffTime || "15",
          automaticBellTest: props.bellOptionsData.automaticBellTest || "N",
          fireBellAction: bellActionFromApi(props.bellOptionsData.fireBellAction),
          burglaryBellAction: bellActionFromApi(props.bellOptionsData.burglaryBellAction),
          supervisoryBellAction: bellActionFromApi(
            props.bellOptionsData.supervisoryBellAction
          ),
          panicBellAction: bellActionFromApi(props.bellOptionsData.panicBellAction),
          emergencyBellAction: bellActionFromApi(
            props.bellOptionsData.emergencyBellAction
          ),
          aux1BellAction: bellActionFromApi(props.bellOptionsData.aux1BellAction),
          aux2BellAction: bellActionFromApi(props.bellOptionsData.aux2BellAction),
          carbonMonoxideAction: bellActionFromApi(props.bellOptionsData.carbonMonoxideAction),
          bellOutput: props.bellOptionsData.bellOutput || "000",
        };
        automaticBellTestEnabled.value = bellOptions.value.automaticBellTest === "Y";
      }
    };

    // Format cutoff time
    const formatCutoffTime = () => {
      if (bellOptions.value.cutOffTime) {
        const formatted = formatToTwoDigits(bellOptions.value.cutOffTime, 1, 99);
        if (formatted) {
          bellOptions.value.cutOffTime = formatted;
        }
      }
    };

    // Format bell output
    const formatBellOutput = () => {
      if (bellOptions.value.bellOutput) {
        bellOptions.value.bellOutput = formatToThreeDigits(
          bellOptions.value.bellOutput,
          0,
          999
        );
      }
    };

    // Construye la sección en forma GET para el template. El GET conserva los
    // códigos cortos (N/S/P/T/4); el form maneja los valores legibles (inverso
    // de bellActionFromApi).
    const buildTemplateItem = () => {
      const toCode = {
        NONE: "N",
        STEADY: "S",
        PULSE: "P",
        TEMPORAL_3: "T",
        TEMPORAL_4: "4",
      };
      const a = (v) => toCode[v] ?? v;
      return {
        ...(props.bellOptionsData || {}),
        cutOffTime: bellOptions.value.cutOffTime,
        automaticBellTest: automaticBellTestEnabled.value ? "Y" : "N",
        fireBellAction: a(bellOptions.value.fireBellAction),
        burglaryBellAction: a(bellOptions.value.burglaryBellAction),
        supervisoryBellAction: a(bellOptions.value.supervisoryBellAction),
        panicBellAction: a(bellOptions.value.panicBellAction),
        emergencyBellAction: a(bellOptions.value.emergencyBellAction),
        aux1BellAction: a(bellOptions.value.aux1BellAction),
        aux2BellAction: a(bellOptions.value.aux2BellAction),
        carbonMonoxideAction: a(bellOptions.value.carbonMonoxideAction),
        bellOutput: bellOptions.value.bellOutput,
      };
    };

    const validateTemplateSection = async () =>
      refFormObserver.value ? await refFormObserver.value.validate() : true;

    // Save bell options
    const saveBellOptions = async () => {
      const isValid = await refFormObserver.value.validate();
      if (!isValid) return;

      if (isTemplateMode.value) {
        emitTemplateUpdate("bell_options", buildTemplateItem());
        toast({
          component: ToastificationContent,
          props: {
            title: proxy.$t("dmp.common.success"),
            text: proxy.$t("dmp.bellOptions.savedSuccessfully"),
            icon: "CheckIcon",
            variant: "success",
          },
        });
        return;
      }

      isSaving.value = true;

      try {
        const payload = {
          cutOffTime: bellOptions.value.cutOffTime,
          automaticBellTest: automaticBellTestEnabled.value ? "Y" : "N",
          fireBellAction: bellOptions.value.fireBellAction,
          burglaryBellAction: bellOptions.value.burglaryBellAction,
          supervisoryBellAction: bellOptions.value.supervisoryBellAction,
          panicBellAction: bellOptions.value.panicBellAction,
          emergencyBellAction: bellOptions.value.emergencyBellAction,
          aux1BellAction: bellOptions.value.aux1BellAction,
          aux2BellAction: bellOptions.value.aux2BellAction,
          carbonMonoxideAction: bellOptions.value.carbonMonoxideAction,
          bellOutput: bellOptions.value.bellOutput,
        };

        const response = await store.dispatch("dmp_panels/setBellOptions", {
          uuid: props.uuid,
          bellOptionsData: payload,
        });

        toast({
          component: ToastificationContent,
          props: {
            title: proxy.$t("dmp.common.success"),
            text: proxy.$t("dmp.bellOptions.savedSuccessfully"),
            icon: "CheckIcon",
            variant: "success",
          },
        });

        // Emit event to notify operation completed
        emit("operation-completed");
      } catch (error) {
        console.error("Error saving bell options:", error);
        emit("operation-error", error);
        toast({
          component: ToastificationContent,
          props: {
            title: proxy.$t("dmp.common.error"),
            text: proxy.$t("dmp.bellOptions.errorSaving"),
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      } finally {
        isSaving.value = false;
      }
    };

    // Watch for changes in props
    watch(
      () => props.bellOptionsData,
      (newData) => {
        if (newData && Object.keys(newData).length > 0) {
          loadBellOptions();
        }
      },
      { deep: true }
    );

    const registerTemplateSection = inject("registerTemplateSection", null);
    const unregisterTemplateSection = inject("unregisterTemplateSection", null);

    onMounted(() => {
      loadBellOptions();
      if (isTemplateMode.value && registerTemplateSection) {
        registerTemplateSection("bell_options", {
          validate: validateTemplateSection,
          collect: buildTemplateItem,
        });
      }
    });

    onBeforeUnmount(() => {
      if (isTemplateMode.value && unregisterTemplateSection) {
        unregisterTemplateSection("bell_options");
      }
    });

    return {
      // Form
      refFormObserver,
      getValidationState,

      // State
      isSaving,
      isLoading,
      automaticBellTestEnabled,
      bellOptions,

      // Options
      bellActionOptions,

      // Methods
      formatCutoffTime,
      formatBellOutput,
      saveBellOptions,
      isTemplateMode,
    };
  },
};
</script>

<style lang="scss" scoped>
.border {
  border: 1px solid #ebe9f1 !important;
}

.dark-layout {
  .border {
    border-color: #404656 !important;
  }
}
</style>
